import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "@/components/base/SEO";
import Breadcrumb from "@/components/base/Breadcrumb";
import SchemaOrg from "@/components/base/SchemaOrg";
import { articles } from "@/mocks/articles";
import { works } from "@/mocks/works";
import { categories } from "@/mocks/site";
import { siteConfig } from "@/mocks/site";

function ArticleSchema({ article }: { article: typeof articles[0] }) {
  const articleSchema = {
    "@type": "Article",
    headline: article.title,
    description: article.summary,
    datePublished: article.date,
    author: { "@type": "Person", name: article.author },
    image: article.image,
    url: `${siteConfig.url}/article/${article.id}`,
  };

  const breadcrumbSchema = {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: siteConfig.url },
      { "@type": "ListItem", position: 2, name: "动态", item: `${siteConfig.url}/category/all` },
      { "@type": "ListItem", position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SchemaOrg type="Article" data={articleSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
    </>
  );
}

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 pt-8 pb-14 text-center">
        <h1 className="text-2xl font-semibold text-foreground-950 mb-4">文章未找到</h1>
        <Link
          to="/"
          className="text-primary-500 hover:text-primary-600 cursor-pointer text-sm"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const relatedArticles = articles.filter(
    (a) => article.relatedArticles?.includes(a.id)
  );

  const relatedWorks = works.filter((w) => {
    const workTitles = ["深夜的诱惑", "月光之城的秘密", "东京恋语", "夏日烟火"];
    return article.title.includes(w.title) || workTitles.some((t) => article.title.includes(t) && w.title === t);
  }).slice(0, 3);

  return (
    <>
      <SEO
        title={`${article.title} - ${siteConfig.name}`}
        description={article.summary}
        keywords={`${article.tags.join(",")},风间由美`}
        canonicalUrl={`${siteConfig.url}/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
      />
      <ArticleSchema article={article} />

      <article className="max-w-4xl mx-auto px-4 md:px-6 pt-8 pb-14">
        <Breadcrumb
          items={[
            { label: "首页", path: "/" },
            { label: "动态", path: "/category/all" },
            { label: article.title },
          ]}
        />

        <header className="mt-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs text-accent-500 bg-accent-100 px-2 py-0.5 rounded-md whitespace-nowrap">
              {article.tags[0]}
            </span>
            <span className="text-sm text-foreground-500">{article.date}</span>
            <span className="text-sm text-foreground-500">· {article.author}</span>
          </div>
          <h1
            className="text-2xl md:text-3xl font-semibold text-foreground-950 leading-snug"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {article.title}
          </h1>
        </header>

        <div className="mb-8 rounded-lg overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full aspect-[2/1] object-cover"
            title={`风间由美 ${article.title}`}
          />
        </div>

        <div
          className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
            prose-h3:text-lg prose-h3:font-semibold prose-h3:text-foreground-950 prose-h3:mt-8 prose-h3:mb-4
            prose-p:mb-4 prose-p:leading-relaxed
            [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-950 [&_h3]:mt-8 [&_h3]:mb-4
            [&_p]:mb-4 [&_p]:leading-relaxed [&_p]:text-foreground-700"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <div className="mt-10 pt-8 border-t border-background-200/60">
          <div className="flex flex-wrap gap-2 mb-8">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>

          {relatedArticles.length > 0 && (
            <div className="mb-8">
              <h3 className="text-base font-semibold text-foreground-950 mb-4">相关文章</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group cursor-pointer p-4 bg-background-100 rounded-lg hover:bg-background-200/70 transition-colors"
                  >
                    <h4 className="text-sm font-medium text-foreground-950 mb-1 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {ra.title}
                    </h4>
                    <p className="text-xs text-foreground-500">{ra.date}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="text-center">
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-s-line"></i> 返回全部动态
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}